import React, { useState, useEffect, useCallback } from 'react';
import {
  FaClipboardList, FaPlus, FaEdit, FaTrash,
  FaSave, FaTimes, FaSpinner, FaSearch
} from 'react-icons/fa';
import empleadosApi from '../../services/api/empleadosApi';

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const bg = { success: '#11998e', error: '#ef4444', warning: '#f59e0b' }[type] || '#667eea';
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      background: bg, color: '#fff', padding: '12px 20px', borderRadius: 10,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 10, maxWidth: 380
    }}>
      <span style={{ flex: 1 }}>{msg}</span>
      <FaTimes style={{ cursor: 'pointer', opacity: 0.8 }} onClick={onClose} />
    </div>
  );
};

// ── Modal CRUD ────────────────────────────────────────────────────────────────
const EMPTY_FORM = { codigo: '', descripcion: '', dias_labores: '', dias_descanso: '', activo: 1 };

const RegimenModal = ({ show, onClose, onSaved, regimen }) => {
  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;
    setForm(regimen
      ? {
          codigo:        regimen.codigo        || '',
          descripcion:   regimen.descripcion   || '',
          dias_labores:  regimen.dias_labores  ?? '',
          dias_descanso: regimen.dias_descanso ?? '',
          activo:        regimen.activo        ?? 1,
        }
      : EMPTY_FORM
    );
    setErrors({});
  }, [show, regimen]);

  const validate = () => {
    const e = {};
    if (!form.codigo.trim())       e.codigo       = 'Requerido';
    if (!form.descripcion.trim())  e.descripcion  = 'Requerido';
    if (form.dias_labores  === '') e.dias_labores  = 'Requerido';
    if (form.dias_descanso === '') e.dias_descanso = 'Requerido';
    return e;
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => { const n = { ...e }; delete n[name]; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dias_labores:  Number(form.dias_labores),
        dias_descanso: Number(form.dias_descanso),
        activo: Number(form.activo),
      };
      if (regimen?.id) {
        await empleadosApi.updateRegimen(regimen.id, payload);
      } else {
        await empleadosApi.createRegimen(payload);
      }
      onSaved(regimen ? 'actualizado' : 'creado');
    } catch (err) {
      setErrors({ _global: err.response?.data?.detail || 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const fld = (label, name, type = 'text', required = false, extra = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <input
        type={type} name={name} value={form[name]} onChange={handleChange}
        min={type === 'number' ? 0 : undefined}
        {...extra}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
          border: `1.5px solid ${errors[name] ? '#ef4444' : '#d1d5db'}`, outline: 'none',
        }}
      />
      {errors[name] && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors[name]}</span>}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '92%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px 16px 0 0', padding: '18px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <h5 style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: 18 }}>
            <FaClipboardList style={{ marginRight: 8 }} />
            {regimen ? 'Editar Régimen' : 'Nuevo Régimen Laboral'}
          </h5>
          <FaTimes onClick={onClose} style={{ color: '#fff', cursor: 'pointer', fontSize: 18 }} />
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {errors._global && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
              {errors._global}
            </div>
          )}

          {fld('Código', 'codigo', 'text', true)}
          {fld('Descripción', 'descripcion', 'text', true)}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {fld('Días de Labores', 'dias_labores', 'number', true)}
            {fld('Días de Descanso', 'dias_descanso', 'number', true)}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Activo
            </label>
            <select name="activo" value={form.activo} onChange={handleChange}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14,
                border: '1.5px solid #d1d5db', background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
              <option value={1}>Sí</option>
              <option value={0}>No</option>
            </select>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12,
            marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '10px 22px', borderRadius: 8, border: '1.5px solid #d1d5db',
                background: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              style={{
                padding: '10px 22px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? 0.8 : 1,
              }}>
              {saving ? <FaSpinner className="fa-spin" /> : <FaSave />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal Confirmar Eliminación ───────────────────────────────────────────────
const ConfirmModal = ({ show, regimen, onConfirm, onCancel, loading }) => {
  if (!show || !regimen) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 32, maxWidth: 420, width: '90%',
        boxShadow: '0 16px 48px rgba(0,0,0,0.25)', textAlign: 'center'
      }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#fef2f2',
          margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaTrash style={{ color: '#ef4444', fontSize: 24 }} />
        </div>
        <h5 style={{ fontWeight: 700, marginBottom: 8 }}>¿Eliminar régimen?</h5>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
          Se eliminará el régimen <strong>{regimen.codigo} — {regimen.descripcion}</strong>.
          Esta acción no se puede deshacer.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={onCancel}
            style={{ padding: '10px 22px', borderRadius: 8, border: '1.5px solid #d1d5db',
              background: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ padding: '10px 22px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: loading ? 0.8 : 1 }}>
            {loading ? <FaSpinner className="fa-spin" /> : <FaTrash />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Componente Principal ──────────────────────────────────────────────────────
const RegimenesLaborales = () => {
  const [regimenes,    setRegimenes]   = useState([]);
  const [loading,      setLoading]     = useState(false);
  const [search,       setSearch]      = useState('');
  const [showModal,    setShowModal]   = useState(false);
  const [editItem,     setEditItem]    = useState(null);
  const [showConfirm,  setShowConfirm] = useState(false);
  const [toDelete,     setToDelete]    = useState(null);
  const [deleting,     setDeleting]    = useState(false);
  const [toast,        setToast]       = useState(null);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const fetchRegimenes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await empleadosApi.getRegimenes(false); // traer todos (activos e inactivos)
      setRegimenes(res.data || []);
    } catch {
      showToast('Error al cargar los regímenes', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRegimenes(); }, [fetchRegimenes]);

  // Filtro local por búsqueda
  const filtered = regimenes.filter(r =>
    !search ||
    r.codigo.toLowerCase().includes(search.toLowerCase()) ||
    r.descripcion.toLowerCase().includes(search.toLowerCase())
  );

  const handleSaved = (action) => {
    setShowModal(false);
    showToast(`Régimen ${action} correctamente`);
    fetchRegimenes();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await empleadosApi.deleteRegimen(toDelete.id);
      showToast('Régimen eliminado correctamente');
      setShowConfirm(false);
      setToDelete(null);
      fetchRegimenes();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al eliminar el régimen';
      showToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container-fluid p-4" style={{ fontFamily: "'Inter', sans-serif" }}>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Tarjeta */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        {/* Header (Inside Card) */}
        <div style={{
            background: 'linear-gradient(135deg,#667eea,#764ba2)',
            color: '#fff', padding: '1.5rem 2rem', border: 'none',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12
        }}>
            <h5 style={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem', margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                <FaClipboardList size={28} />
                Régimenes Laborales
            </h5>
            <button onClick={() => { setEditItem(null); setShowModal(true); }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = 'linear-gradient(135deg,#38ef7d,#2dd4bf)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = 'linear-gradient(135deg,#11998e,#38ef7d)'; }}
                style={{
                    background: 'linear-gradient(135deg,#11998e,#38ef7d)',
                    border: 'none', color: '#fff', borderRadius: 12, padding: '10px 16px',
                    fontWeight: 600, boxShadow: '0 4px 14px rgba(17,153,142,.3)',
                    transition: 'all .3s', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14
                }}>
                <FaPlus /> Nuevo Régimen
            </button>
        </div>

        {/* Barra búsqueda */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
            <FaSearch style={{ position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />
            <input
              type="text" placeholder="Buscar por código o descripción…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10,
                border: '1.5px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
            {search && (
              <FaTimes onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 14, top: '50%',
                  transform: 'translateY(-50%)', color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}
              />
            )}
          </div>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            {loading ? 'Cargando…' : `${filtered.length} régimen${filtered.length !== 1 ? 'es' : ''}`}
          </span>
        </div>

        {/* Tabla */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                {['#', 'Código', 'Descripción', 'Días Labores', 'Días Descanso', 'Ciclo Total', 'Activo', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left',
                    fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: 13 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <FaSpinner className="fa-spin" style={{ fontSize: 28, color: '#667eea' }} />
                  <p style={{ color: '#9ca3af', marginTop: 8 }}>Cargando regímenes…</p>
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 60 }}>
                  <FaClipboardList size={48} style={{ color: '#d1d5db', marginBottom: 12 }} />
                  <p style={{ color: '#9ca3af', margin: 0, fontWeight: 500 }}>
                    {search ? 'No se encontraron regímenes con esa búsqueda' : 'No hay regímenes registrados'}
                  </p>
                  {!search && (
                    <button onClick={() => { setEditItem(null); setShowModal(true); }}
                      style={{ marginTop: 16, background: 'linear-gradient(135deg,#667eea,#764ba2)',
                        color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                        fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      <FaPlus style={{ marginRight: 6 }} />Agregar primer régimen
                    </button>
                  )}
                </td></tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id}
                    style={{ borderBottom: '1px solid #f3f4f6',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}
                  >
                    <td style={{ padding: '11px 16px', color: '#9ca3af', fontWeight: 600 }}>{idx + 1}</td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ background: '#ede9fe', color: '#7c3aed',
                        borderRadius: 6, padding: '4px 12px', fontWeight: 700, fontSize: 13 }}>
                        {r.codigo}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#111827' }}>{r.descripcion}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>
                      {r.dias_labores}
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>
                      {r.dias_descanso}
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      <span style={{ background: '#e0f2fe', color: '#0369a1',
                        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                        {r.dias_labores + r.dias_descanso} días
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        background: r.activo ? '#d1fae5' : '#f3f4f6',
                        color: r.activo ? '#065f46' : '#6b7280',
                        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700
                      }}>
                        {r.activo ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => { setEditItem(r); setShowModal(true); }}
                          style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)',
                            color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px',
                            cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FaEdit /> Editar
                        </button>
                        <button onClick={() => { setToDelete(r); setShowConfirm(true); }}
                          style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                            color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px',
                            cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RegimenModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSaved={handleSaved}
        regimen={editItem}
      />
      <ConfirmModal
        show={showConfirm}
        regimen={toDelete}
        onConfirm={handleDelete}
        onCancel={() => { setShowConfirm(false); setToDelete(null); }}
        loading={deleting}
      />
    </div>
  );
};

export default RegimenesLaborales;
